import SchemaServiceI from 'tuc-webui-base/services/SchemaService';

const mergeEntities = (entities1, entities2) => {
  // This function assumes provided entity arrays are immutable
  if (!entities1) return entities2 || [];
  if (!entities2) return entities1;
  return entities1.concat(entities2.filter(name => (entities1.indexOf(name) < 0)));
};

class SchemaService extends SchemaServiceI {
  constructor(configuration) {
    super();
    this.schema = configuration.schema;
  }

  getPaths() {
    return this.schema.paths;
  }

  getBasePath() {
    return this.schema.basePath;
  }

  getEntity(entityName) {
    return this.schema.entities[entityName];
  }

  getType(typeName) {
    return this.schema.definitions[typeName];
  }

  isEnum(type) {
    return Boolean(type.enum);
  }

  getEnumNamedValues(type) {
    return type.enum;
  }

  getPropertyNames(type, includeHiddenProperties = false) {
    const { properties } = type;
    if (includeHiddenProperties) return properties;
    const visibleProps = [];
    Object.keys(properties).forEach((name) => {
      if (!properties[name].hidden) {
        visibleProps.push(name);
      }
    });
    return visibleProps;
  }

  getProperties(type, includeHiddenProperties = false) {
    const { properties } = type;
    const propInstances = Object.keys(properties).map((name) => {
      return { ...properties[name], name };
    });
    return includeHiddenProperties ? propInstances : propInstances.filter(p => !p.hidden);
  }

  getPathSchema(path) {
    const info = this.schema.paths[path].get;
    return info && info.result && info.result.schema;
  }

  // Default value
  getDefaultValue(prop, data, context) {
    const { defaultValue } = prop;
    const { idProperty } = context || {};
    if (defaultValue !== undefined) {
      if (typeof (defaultValue) === 'function') {
        return defaultValue(data);
      }
      return defaultValue;
    }
    if ((prop.name === idProperty) || (prop.name === '_id')) {
      if (data && Array.isArray(data) && data.length) {
        const usedIds = data.map(obj => obj[prop.name]);
        let counter;
        let value;
        if ((prop.type === 'integer') || (prop.type === 'number')) {
          counter = 1;
          do {
            value = data[0] + counter;
            counter += 1;
          } while (usedIds.indexOf(value) >= 0);
        } else {
          // Remove trailing numbers from id of first data element
          const prefix = data[0][prop.name].replace(/\d+$/, '');
          counter = 2;
          do {
            value = `${prefix}${counter}`;
            counter += 1;
          } while (usedIds.indexOf(value) >= 0);
        }
        return value;
      }
      if ((prop.type === 'integer') || (prop.type === 'number')) {
        return 0;
      }
      return 'default';
    }
    if ((prop.type === 'integer') || (prop.type === 'number')) {
      return 0;
    }
    if (prop.type === 'string') {
      return '';
    }
    if (prop.type === 'array') {
      return [];
    }
    const schemaType = this.getType(prop.type);
    if (schemaType) {
      if (schemaType.enum) {
        return schemaType.enum[0].value;
      }
    }
    return {};
  }

  //
  // Validation
  //
  validateValue(value, prop, data, context) {
    const { validators } = prop;
    if (!validators) {
      return '';
    }
    for (let i = 0; i < validators.length; i += 1) {
      const msg = validators[i](value, data, prop.name, context);
      if (msg) return msg;
    }
    return '';
  }

  //
  // Computed properties
  //
  addComputedProperty({ name, entities, typename, ...prop }) {
    const type = this.getType(typename);
    type.requiredEntities = mergeEntities(type.requiredEntities, entities);
    type.properties[name] = prop;
    type.computedProperties = type.computedProperties || [];
    type.computedProperties.push({ name, compute: prop.compute });
  }

  getComputedProperties(type) {
    return type.computedProperties;
  }

  getRequiredEntities(type) {
    return type && type.requiredEntities;
  }
}

export { SchemaService as default, mergeEntities };
